import { creativeErrorMessage } from '@/utils/creative-error-message'
import { memo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import CreativeAdPreview, { CreativeHoverPreview } from '@/pages/campaigns/creatives/components/CreativeAdPreview'
import { HoverableMediaPreview } from '@/components/ui/hoverable-media-preview'
import { Badge } from '@/components/ui/badge'
import { LeadFormIcon } from '@/components/icons'
import { EditIcon, CopyIcon, TrashIcon, InfoCircleIcon } from '@/components/icons/new'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { usePageData } from '@/components/mediaBuying/adCreatives/hooks'
import { normalizeCreativeFormat, parseJsonList, processCreativeAssets } from '@/utils/creative-preview'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

// Utility function to get error message for failed creatives
const getCreativeErrorMessage = (creative) => {
    if (creative.status === 'FAILED' && creative.error) {
        const reason = creativeErrorMessage(creative.error)
        return `The Ad creative creation failed.${reason ? ` ${reason}` : ''}`
    }
    return 'Ad creative rejected. One or more of your assets did not pass moderation. Please replace the rejected assets and resubmit for review.'
}

const CreativeCard = memo(
    ({
        creative,
        isSelected,
        onToggleSelection,
        onDelete = null,
        hideCheckbox = false,
        campaignId,
        isSelectable = true,
    }) => {
        const navigate = useNavigate()
        const [showDeleteModal, setShowDeleteModal] = useState(false)
        const { connectedPublishers } = useMediaBuyingContext()
        const assetVariations = creative.adAssets.flatMap((asset) => asset.variations || [])

        // Fetch page data for the ad preview
        const { pageTitle, pageIcon } = usePageData(connectedPublishers)

        const normalizedFormat = normalizeCreativeFormat(creative.format)

        // Media used for the hover-enlarge preview (first asset for carousel format)
        const { assetData, carouselAssets } = processCreativeAssets(creative.adAssets || [], normalizedFormat)
        const previewAsset = normalizedFormat === 'carousel' ? carouselAssets[0] : assetData

        const handleCardClick = useCallback(
            (e) => {
                // Don't toggle selection if clicking action buttons or modal
                if (
                    e.target.closest('.delete-button') ||
                    e.target.closest('.clone-button') ||
                    e.target.closest('.modal')
                ) {
                    return
                }
                if (!isSelectable) return
                onToggleSelection(creative.id)
            },
            [creative.id, onToggleSelection, isSelectable],
        )

        const handleDeleteClick = useCallback((e) => {
            e.stopPropagation()
            setShowDeleteModal(true)
        }, [])

        const handleConfirmDelete = useCallback(async () => {
            if (!onDelete) return

            try {
                await onDelete(creative.id)
                setShowDeleteModal(false)
            } catch (error) {
                console.error('Error deleting creative:', error)
                toast.error('Failed to delete creative')
            }
        }, [creative.id, onDelete])

        const handleEditClick = useCallback(
            (e) => {
                e.stopPropagation()
                if (campaignId) {
                    navigate(`/campaigns/media-buying/${campaignId}/edit-creative/${creative.id}`)
                }
            },
            [creative.id, campaignId, navigate],
        )

        const handleCloneClick = useCallback(
            (e) => {
                e.stopPropagation()
                if (campaignId) {
                    navigate(`/campaigns/media-buying/${campaignId}/clone-creative/${creative.id}`)
                }
            },
            [creative.id, campaignId, navigate],
        )

        const parsedTags = parseJsonList(creative.tags)

        if (!creative || assetVariations.length === 0) return

        // Enlarged preview of just the ad creative (the white mockup card) shown on hover
        const previewContent = <CreativeHoverPreview creative={creative} pageTitle={pageTitle} pageIcon={pageIcon} />

        return (
            <>
                <div
                    className={`creative-card tw:relative tw:h-[506px] tw:max-h-[506px] tw:w-[300px] tw:rounded-sm tw:bg-[#17273C] tw:cursor-pointer tw:transition-all tw:duration-200 tw:ease-in-out tw:overflow-hidden tw:flex tw:flex-col tw:hover:tw:scale-[1.03] tw:hover:tw:shadow-2xl tw:hover:tw:z-20 ${isSelected ? 'selected' : ''}`}
                    onClick={handleCardClick}
                >
                    {/* Selection Checkbox */}
                    {!hideCheckbox && isSelectable && (
                        <div
                            className={`tw:absolute tw:top-3.5 tw:left-3 tw:z-10 tw:w-5 tw:h-5 tw:rounded tw:flex tw:items-center tw:justify-center ${
                                isSelected
                                    ? 'tw:border-1 tw:border-transparent tw:bg-[#2450BF]'
                                    : 'tw:border-1 tw:border-[#2450BF] tw:bg-transparent'
                            }`}
                        >
                            {isSelected && (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path
                                        d="M10 3L4.5 8.5L2 6"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </div>
                    )}

                    {/* Status Icons - positioned to the right side */}
                    <div className="tw:absolute tw:top-3 tw:right-3 tw:z-10 tw:flex tw:gap-1">
                        {creative.lead_form_id && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <LeadFormIcon />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <span>Lead form is attached to this creative</span>
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>

                    <div className="tw:flex-1 tw:min-h-0 tw:w-full tw:flex tw:flex-col">
                        {/* Creative Title */}
                        <div
                            className={`tw:p-3 tw:pb-2 tw:text-white tw:text-sm tw:font-semibold tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap ${!hideCheckbox && isSelectable ? 'tw:ml-7' : ''}`}
                        >
                            {creative.name || 'Untitled Creative'}
                        </div>

                        {/* Ad Preview - Large and prominent */}
                        <div className="tw:flex-1 tw:flex tw:justify-center tw:items-start tw:overflow-hidden tw:!relative">
                            <div className="tw:scale-[0.70] tw:origin-top tw:flex tw:justify-center">
                                {/* Enlarged preview only when hovering the ad mockup, not the action buttons */}
                                <HoverableMediaPreview
                                    src={previewAsset?.url}
                                    mediaType={previewAsset?.type}
                                    disabled={!previewAsset?.url}
                                    previewContent={previewContent}
                                    thumbnailClassName="tw:hover:tw:scale-100 tw:hover:tw:brightness-100"
                                >
                                    <CreativeAdPreview
                                        creative={creative}
                                        pageTitle={pageTitle}
                                        pageIcon={pageIcon}
                                        autoPlay
                                    />
                                </HoverableMediaPreview>
                            </div>
                        </div>

                        {/* Tags */}
                        {parsedTags.length > 0 && (
                            <div className="tw:px-3 tw:pt-3 tw:pb-2 tw:flex tw:flex-wrap tw:gap-1.5 tw:items-center">
                                {parsedTags.slice(0, 4).map((tag, index) => (
                                    <Badge
                                        key={index}
                                        variant="campaign"
                                        className="tw:text-sm tw:text-[#fff] tw:px-2.5 tw:py-0.5"
                                    >
                                        {tag}
                                    </Badge>
                                ))}
                                {parsedTags.length > 4 && (
                                    <span className="tw:text-[#8b949e] tw:text-xs tw:font-medium">
                                        +{parsedTags.length - 4} more
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Status Badge and Delete Button */}
                        <div className="tw:px-3 tw:pb-3 tw:flex tw:items-center tw:justify-between">
                            {creative.status === 'FAILED' ? (
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div>
                                                <InfoCircleIcon size={24} className="tw:text-[#E0295E]" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="tw:max-w-80">
                                            <p>{getCreativeErrorMessage(creative)}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <span className="tw:text-sm tw:font-normal tw:text-[#E0295E]">Rejected</span>
                                </div>
                            ) : creative.status === 'PROCESSING' ? (
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div>
                                                <InfoCircleIcon size={24} className="tw:text-yellow-500" />
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="tw:max-w-80">
                                            <p>
                                                Your ad creative is being processed and uploaded to the advertising
                                                platform. This usually takes a few moments.
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <span className="tw:text-sm tw:font-normal tw:text-yellow-500">Processing</span>
                                </div>
                            ) : (
                                <span
                                    className={`tw:text-sm tw:font-normal ${
                                        creative.is_used ? 'tw:text-[#4DD15E]' : 'tw:text-[#899CBD]'
                                    }`}
                                >
                                    {creative.is_used ? 'Used' : 'Not used'}
                                </span>
                            )}

                            <div className="tw:flex tw:items-center tw:gap-1">
                                {creative.status !== 'PROCESSING' && campaignId && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="tw:bg-transparent tw:border-none tw:cursor-pointer tw:p-1 tw:flex tw:items-center tw:justify-center tw:text-blue-500"
                                                onClick={handleEditClick}
                                            >
                                                <EditIcon />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Edit creative</TooltipContent>
                                    </Tooltip>
                                )}
                                {creative.status !== 'PROCESSING' && campaignId && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="clone-button tw:bg-transparent tw:border-none tw:cursor-pointer tw:p-1 tw:flex tw:items-center tw:justify-center tw:text-blue-500"
                                                onClick={handleCloneClick}
                                            >
                                                <CopyIcon />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Clone creative</TooltipContent>
                                    </Tooltip>
                                )}
                                {creative.status !== 'PROCESSING' && onDelete && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="delete-button tw:bg-transparent tw:border-none tw:cursor-pointer tw:p-1 tw:flex tw:items-center tw:justify-center tw:text-blue-500"
                                                onClick={handleDeleteClick}
                                            >
                                                <TrashIcon />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Delete creative</TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <ConfirmationModal
                    show={showDeleteModal}
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={handleConfirmDelete}
                    title="Delete Ad creative"
                    body={`Are you sure you want to delete "${creative.name || 'Untitled Creative'}"? This action cannot be undone.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    variant="danger"
                />
            </>
        )
    },
)

CreativeCard.displayName = 'CreativeCard'

export default CreativeCard
